import Digest from "../util/digest";
import ValidateModule from "./ValidateModule";

import type {Attribute, SortOrder} from "@esWebAPI/es/model/Feature";
import type {Criteria} from "../model/Criteria";
import type {LayerInfo} from "@esWebAPI/es/model/Layer";
import type {Feature, IUpdateAction, IEditResult, ActionType} from "@esWebAPI/es/model/Feature";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { ApiOpts, IEventChannel } from "../model/Models";
import type CacheModule from "./CacheModule";

/** @class es.module.[resource]
     * Object providing [resource].
     * @param eventChannel
     * @param rqBroker
     */
    export default class FeatureModule {

        eventChannel: IEventChannel;
        readonly rqBroker: AjaxRequestBroker;
        readonly resourceName: string;
        readonly cache: CacheModule;
        mapTileServerIndex: number;
        lowLoadOverlayBoundsByRes: {[key: number] : {minX: number, minY: number, maxX: number, maxY: number}};

        constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName = "Feature", cache: CacheModule) {
            this.eventChannel = eventChannel;
            this.rqBroker = rqBroker;
            this.resourceName = resourceName;
            this.cache = cache;
            this.mapTileServerIndex = 0;
            this.lowLoadOverlayBoundsByRes = {};// TODO: 1) use cache instead? 2) blitz when layer added/removed/updated

            this.eventChannel.addListener("onDestroy", () => {
                    this.eventChannel = null; // prevent memory leaks when the map widget is destroyed
                });
        }

        public get(layerViewId: number, featureId: number, requiredAttrs?: string[], dynamicAttrs?: string, srid?: number, opts?: ApiOpts) {
            if (!ValidateModule.checkId(layerViewId) ||
                !ValidateModule.checkId(featureId)) {
                return es.util.deferred.rejected();
            }

            let url: any[] = [es.api.getURL(this.resourceName), "/", layerViewId, "/", featureId];
            let addedParam = false;
            if (requiredAttrs) {
                url.push("?requiredAttrs=", requiredAttrs);
                addedParam = true;
            }
            if (dynamicAttrs) {
                url.push((addedParam ? "&" : "?"), "dynamicAttrs=", es.util.url.stringify(dynamicAttrs));
                addedParam = true;
            }
            if (srid) {
                url.push((addedParam ? "&" : "?"), "srid=", srid.toString());
                addedParam = true;
            }
            const urlString = url.join("");

            let getFeatureDfd;
            if (urlString.length > this.rqBroker.MAX_URL_LENGTH) {
                const postURL = es.api.getURL(this.resourceName) + "/" + layerViewId +  "/" + featureId;
                const postData = {};
                if (requiredAttrs) postData.requiredAttrs = requiredAttrs;
                if (dynamicAttrs) postData.dynamicAttrs = dynamicAttrs;
                // if (srid) postData.srid = srid; // actually not in use, removed in TOR9-19134
                
                getFeatureDfd = this.rqBroker.doPost({url: postURL, data: postData, opts});
            } else {
                getFeatureDfd = this.rqBroker.doGet({url: urlString, opts})
            }

            return $.when(
                getFeatureDfd,
                FeatureModule.loadAttrTypeMap(layerViewId)
            ).then(function(feature: Feature, attrTypeMap: typeof FeatureModule.createAttrTypeMap) {
                FeatureModule.processFeature(feature, attrTypeMap);

                return feature;
            });
        }

        private static loadAttrTypeMap(layerViewId: number): Promise<typeof FeatureModule.createAttrTypeMap>{
            return es.api.layerInfo.getAll([{layerViewId}]).then(function(layerInfos) {
                return FeatureModule.createAttrTypeMap(layerInfos[0]);
            }) as unknown as Promise<any>;
        }

        private static createAttrTypeMap(layerInfo: LayerInfo){
            const map: {[key: string]: Attribute["attrType"]} = {};
            const attrs = layerInfo.attributes;
            for(let a = attrs.length; a-- > 0;){
                const attr = attrs[a];
                map[attr.name] = attr.attrType;
            }
            return map;
        }

        private static processFeature(feature: Feature, attrTypeMap:any){
            if(feature){
                const attrs = feature.attrs;
                for (const attrName in attrs) {
                    const value = attrs[attrName];
                    if(value != null){
                        attrs[attrName] = FeatureModule.normalize(value, attrTypeMap[attrName]);
                    }
                }
            }
        }

        private static normalize(value: any, type: Attribute["attrType"]){
            if(value != null){
                switch(type){
                    case "TIMESTAMP":
                        return new Date(value);
                    case "BOOLEAN":
                        return !!value;
                    default:
                        return value;
                }
            }
        }

        /**
         * Convenience method to fetch array of feats from multiple layerViews
         *
         * @param featKeys [ {lvId: x, featIds: [y, z, ...] } or ['lvId_featId', ...]
         * @param opts
         * e.g. es.api.feature.getByFeatKeys( [ {"lvId": 206, "featIds": [1, 2, 3]}, {"lvId": 216, "featIds": [4, 5, 6]} ] ).done(console.log)
         *      or
         *      es.api.feature.getByFeatKeys( ["410_184", "410_48", "410_25"] ).done(console.log)
         */
        public getByFeatKeys(featKeys: {lvId: number, featIds: number[]}[] | string[], opts?: ApiOpts) {
            if(!Array.isArray(featKeys)){
                throw new Error("getByFeatKeys :: featKeys must be an array: " + JSON.stringify(featKeys));
            }

            if(featKeys.length === 0){
                return es.util.deferred.resolved([]);
            }

            let featureKeysMap: {lvId: number, featIds: number[]}[] = [];

            // featKeys passed as string underscore composite keys e.g. ["410_184"]
            if(typeof featKeys[0] === 'string' && featKeys[0].includes("_")){
                const explodedFeatKeys: {lvId: number, featIds: number[]}[] = [];

                (featKeys as string[]).forEach(function(item: string){
                    const [lvId, featId] = item.split("_");
                    let featKeyForLvId = explodedFeatKeys.find(fk => fk.lvId == +lvId)
                    if (featKeyForLvId) {
                        featKeyForLvId.featIds.push(+featId);
                    } else {
                        featKeyForLvId = {"lvId": +lvId, "featIds": [+featId]};
                        explodedFeatKeys.push(featKeyForLvId);
                    }
                });

                featureKeysMap = explodedFeatKeys;
            } else {
                featureKeysMap = featKeys as {lvId: number, featIds: number[]}[];
            }

            const dfds = [];
            let allFeats: Feature[] = [];
            for (const featKey of featureKeysMap) {
                dfds.push(
                    this.getByIds(featKey.lvId, featKey.featIds, null, undefined, opts).then(function(feats : Feature[]){
                        allFeats = allFeats.concat(feats);
                    })
                );
            }
            return $.when(...dfds).then(function(){
                return allFeats;
            });
        }

        public getByIds(layerViewId: number, featureIds: number[], requiredAttrs?: null|string[], srid?: number, opts?: ApiOpts) : JQuery.Promise<Feature[]> {
            // TODO: we need to support POST here as featIds can get very large or enforce a limit
            if (!ValidateModule.checkId(layerViewId) ||
                !ValidateModule.checkIds(featureIds)) {
                return es.util.deferred.rejected();
            }

            const url = [es.api.getURL(this.resourceName), "/ids?layerViewId=", layerViewId, "&featureIds="+ es.util.url.stringify(featureIds)];

            if (requiredAttrs) {
                url.push("&requiredAttrs=", requiredAttrs);
            }
            if (srid) {
                url.push("&srid=", srid);
            }
            const urlString = url.join("");

            return $.when(
                this.rqBroker.doGet({url: urlString, opts}),
                FeatureModule.loadAttrTypeMap(layerViewId)
            ).then((features: Feature[], attrTypeMap: any) => {
                for(let f = features.length; f-- > 0;){
                    FeatureModule.processFeature(features[f], attrTypeMap);
                }
                return features;
            });
        }

         private static getUnmarshalledFeatures(layerViewId: number, featureData: any[], styles: any, attrNames: string[], attrTypes:{[attrName: string]: Attribute["attrType"]}, getCenters: boolean) {
            //create attrs factory here...
            const attrsFactory = attrNames ? FeatureModule.createFactory(attrNames) : null;
            const numAttrs = attrNames ? attrNames.length : 0;

            const features: Feature[] = [];
            const featureDataLen = featureData.length;
            let f = 0;
            while (f < featureDataLen) {
                const id = featureData[f++];
                const geom = featureData[f++];
                const center = getCenters ? featureData[f++] : null;
                const style = styles[featureData[f++]]; // Warning these styles are shared by the features
                let attrs = null;
                const label = featureData[f++];

                if (attrsFactory) {
                    attrs = attrsFactory();
                    for (let a = numAttrs; a-- > 0; ) {
                        const attrName = attrNames[a];
                        let attrValue = featureData[f + a];
                        if (attrValue != null){
                            attrValue = FeatureModule.normalize(attrValue, attrTypes[attrName]);
                        }
                        attrs[attrName] = attrValue;
                    }
                    f += numAttrs;
                }

                features.push({
                    id: id,
                    layerViewId : layerViewId,
                    geom: geom,
                    center : center,
                    style: style,
                    attrs: attrs,
                    label : label
                });
            }

            return features;
        }

        private unmarshalFeatures (featureViewResponse: {layers: any, styles: any, processed?: boolean}, layerInfos: LayerInfo[], getCenters: boolean) {
            const rs = {
                'layers': featureViewResponse.layers,
                'styles': featureViewResponse.styles
            };

            if (featureViewResponse.processed) {
                return rs;
            }

            const layers = rs.layers;
            const styles = rs.styles; // get list of style objects returned with data
            const unmarshalledFeatures = [];
            for (let l = 0; l < layers.length; l++) {
                const layer = layers[l];
                const layerInfo = layerInfos[l];

                const layerViewId = layer.layerViewId;
                const featureData = layer.features;
                const attrNames = layer.attrs;
                const attrTypes = FeatureModule.createAttrTypeMap(layerInfo);
                //var labelAttr = layer.labelAttr;
                //var labelType = attrTypes[layer.labelAttr];

                const uf = FeatureModule.getUnmarshalledFeatures(layerViewId, featureData, styles, attrNames, attrTypes, getCenters);
                unmarshalledFeatures.push(uf);
            }

            // only change the featureViewResponse structure when all is done, just in case...
            for (let l = 0; l < layers.length; l++) {
                layers[l].features = unmarshalledFeatures[l];
            }

            featureViewResponse.processed = true;

            return rs;
        }


        // TODO: avoidPost param added for TOR9-1871 - not sure this is required anymore. any way to check (other than remove it and see what happens)?
        private getDataDfd(layerData: any, avoidPost: boolean, originOfRequest: string, opts?: ApiOpts): JQuery.Deferred<any>{
            const getUrl = es.util.URLON.toUrl(layerData, es.api.getURL("featureview/" + layerData.format.$type + originOfRequest)+"?");
            if (getUrl.length <= this.rqBroker.MAX_URL_LENGTH) {  // simple get request
                return this.rqBroker.doGet({
                    'url': getUrl,
                    'opts': opts
                });
            }

            if (!avoidPost) {
                return this.rqBroker.doPost({
                    url: es.api.getURL("featureview/" + layerData.format.$type + originOfRequest),
                    data : layerData,
                    opts: opts
                });
            }

            // url is too long, but we are avoiding posts - so we create a bucket
            let bucketParams = $.extend({}, layerData);
            delete bucketParams.version;
            delete bucketParams.TENANT_ID;
            delete bucketParams.sizePx;
            delete bucketParams.view;
            bucketParams = JSON.stringify(bucketParams);

            const key = new Digest().updateString(bucketParams).finish();
            const bucketUrl = {
                'key': key,
                'version': layerData.version,
                'sizePx': undefined,
                'view': undefined,
                'TENANT_ID': undefined
            };
            if(layerData.sizePx){
                bucketUrl.sizePx = layerData.sizePx;
            }
            if(layerData.view){
                bucketUrl.view = layerData.view;
            }
            if(layerData.TENANT_ID){
                bucketUrl.TENANT_ID = layerData.TENANT_ID;
            }

            return es.api.bucket.getOrCreateBucket(key, bucketParams).then(() => {
                const url = es.util.URLON.toUrl(bucketUrl, es.api.getURL("featureview/" + layerData.format.$type + originOfRequest) + "?");
                console.log("Using bucket (avoidPost=true) for featureview request", url);
                return this.rqBroker.doGet({
                    'url': url,
                    'opts': opts
                });
            });
        }

        private mergeResults = function() {
            const results = Array.prototype.slice.call(arguments);

            if (results.length == 1) {
                return results[0];
            }

            const rs = {
                'layers': [],
                'styles': []
            };

            for (let i=0; i<results.length; i++) {
                rs.layers.push.apply(rs.layers, results[i].layers); // No need to adjust styles index, as layers have already been converted
                rs.styles.push.apply(rs.styles, results[i].styles); // TODO FIXME Do we need this?! layers don't reference it anymore...
            }

            return rs;
        }

        /**
         * Renders feature objects in JSON for the parameters given. At a minimum, the parameters should specify a geo
         *
         * @param params
         * @param avoidPost flag that if set to true means that post requests will be avoided - if a url is too long, a bucket will be created on the server side to handle the data
         * @param opts
         * @return a jquery deferred object resolved when the list is available
         */
        public render(params: unknown, avoidPost?: boolean, opts?: ApiOpts) {
            const data = es.util.object.deepCopy(params);
            if (!data.format) {
                data.format = {};
            }

            data.format.$type = "JsonFormat";
            if(!data.format.getAttrs && (data.format.getAttrs !== false)){
                data.format.getAttrs = true;
            }

            let originOfRequest = "";
            if(data["originOfRequest"]){
                originOfRequest = "_" + data["originOfRequest"];
                delete data.originOfRequest;
            }

            return this.getDataDfd(data, avoidPost, originOfRequest, opts).then((rs: any) => {
                const layerInfos = rs.layers.map(function (layer: any) {
                    return {
                        'layerViewId': layer.layerViewId,
                        'layerMetaId': layer.layerMetaId
                    };
                });

                return es.api.layerInfo.getAll(layerInfos).then((layerInfos) => {
                    return this.unmarshalFeatures(rs, layerInfos, data.format.getCenters);
                });
            }).then(this.mergeResults);
        }

        /**
         * @private
         * @deprecated
         */
        public getMapTileServerURL(){
            if(this.mapTileServerIndex == es.mapTileServers.length-1){
                this.mapTileServerIndex = 0;
            } else {
                this.mapTileServerIndex++;
            }
            //es.log.info(es.mapTileServers[mapTileServerIndex]);
            return es.mapTileServers[this.mapTileServerIndex];
        }

        /**
         * Apply the edits given.
         *
         * @return a jquery deferred object resolved when the list is available
         */
        public edit(edits: ActionType[], opts?: ApiOpts) {
            edits = edits.slice();
            for(let e = edits.length; e-- > 0;){
                edits[e] = FeatureModule.formatForUpdate(edits[e]);
            }
            return this.rqBroker.doPost({
                url : es.api.getURL(this.resourceName)+"/edit",
                opts: opts,
                data : edits
            }).then(async (editResults: IEditResult<ActionType>[]) =>{
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerInfo_LayerMeta");
                this.cache.clear("Thematic");

                if (!this.eventChannel) {
                    return editResults;
                }
                let lastModified = -1;
                for(let e = 0; e < editResults.length; e++){
                    // FIXME :: I can't figure out the correct structure that it is sent here
                    const result: any = editResults[e];
                    lastModified = Math.max(lastModified, result.featureSourceInfoLastModified);
                    if(result.action.layerViewId){ // only layer view features fire these events...
                        switch(result.action.$type){
                            case "Create":
                                await this.eventChannel.fireEvent("onCreateFeature", {
                                    id : result.featureId,
                                    layerViewId : result.action.layerViewId,
                                    attrs : result.action.attrs,
                                    geom : result.action.geom,
                                    srid : result.action.srid,
                                    label : result.label
                                });
                                break;
                            case "Update":
                                await this.eventChannel.fireEvent("onUpdateFeature", {
                                    id : result.action.featureId,
                                    layerViewId : result.action.layerViewId,
                                    attrs : result.action.attrs,
                                    geom : result.action.geom,
                                    srid : result.action.srid,
                                    label : result.label
                                });
                                break;
                            case "Remove":
                                await this.eventChannel.fireEvent("onRemoveFeature", result.action.featureId, result.action.layerViewId);
                                break;
                        }
                    }
                }
                await this.eventChannel.fireEvent("onEditFeatures", editResults, lastModified);
                return editResults;
            });
        }

        /**
         * Create a feature
         * @deprecated ?
         */
        public create(feature: Feature) {
            if (!ValidateModule.checkMandatoryArg(feature)) {
                return es.util.deferred.rejected();
            }

            //FIXME :: What type is here?
            feature = FeatureModule.formatForUpdate(feature as unknown as ActionType) as any;
            return this.rqBroker.doPost({
                url : es.api.getURL(this.resourceName),
                data : feature
            }).then((feature: Feature) => {
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerInfo_LayerMeta");
                this.cache.clear("Thematic");

                if (!this.eventChannel) {
                    return feature;
                }

                this.eventChannel.fireEvent("onCreateFeature", feature);

                return Promise.all([
                    es.api.layerView.get(feature.layerViewId),
                    es.api.layerInfo.getForLayerView(feature.layerViewId)
                ]).then(([layerView, layerInfo]) => {
                    return es.api.geoMap.get(layerView.geoMapId).then((geoMap) => {
                        this.eventChannel.fireEvent("onEditFeatures", [{
                            $type : "Create",
                            featureId : feature.id,
                            action : {
                                layerViewId : feature.layerViewId,
                                attrs : feature.attrs,
                                geom : feature.geom,
                                srid : geoMap.srid
                            }
                        }],
                        layerInfo.featureInfo.lastModified);

                        return feature;
                    });
                });
            });
        }

        /**
         * Update a feature
         * @deprecated Not used
         */
        public update (updateAction: IUpdateAction, opts?: ApiOpts): JQueryPromise<IUpdateAction> {
            if (!ValidateModule.checkMandatoryArg(updateAction)) {
                return es.util.deferred.rejected();
            }

            updateAction = FeatureModule.formatForUpdate(updateAction) as IUpdateAction;
            // FIXME :: is it is or featureId??
            return this.rqBroker.doPut({
                // @ts-ignore
                url : es.api.getURL(this.resourceName) + "/" + updateAction.layerViewId + "/" + updateAction.id,
                opts: opts,
                data : updateAction
            }).then(() => {
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerInfo_LayerMeta");
                this.cache.clear("Thematic");

                if (!this.eventChannel) {
                    return updateAction;
                }

                this.eventChannel.fireEvent("onUpdateFeature", updateAction);
                return Promise.all([
                    es.api.layerView.get(updateAction.layerViewId),
                    es.api.layerInfo.getForLayerView(updateAction.layerViewId)
                ]).then(([layerView, layerInfo]) => {
                    return es.api.geoMap.get(layerView.geoMapId).then((geoMap: any) => {
                        this.eventChannel.fireEvent("onEditFeatures", [{
                            $type : "Update",
                            action : {
                                featureId : updateAction.id,
                                layerViewId : updateAction.layerViewId,
                                attrs : updateAction.attrs,
                                geom : updateAction.geom,
                                srid : geoMap.srid
                            }
                        }],
                        layerInfo.featureInfo.lastModified);

                        return updateAction;
                    });
                });
            });
        }

        /**
         * Remove a feature
         */
        public remove (featureId: number, layerViewId: number, opts?: ApiOpts) : JQueryDeferred<void> {
            if (!ValidateModule.checkId(featureId) ||
                !ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doDelete( {url: es.api.getURL(this.resourceName) + "/" + layerViewId + "/" + featureId, opts: opts} ).done(() =>{
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerInfo_LayerMeta");
                this.cache.clear("Thematic");

                if (!this.eventChannel) {
                    return;
                }

                this.eventChannel.fireEvent("onRemoveFeature", featureId, layerViewId);
                es.api.layerInfo.getForLayerView(layerViewId).then((layerInfo) => {
                    this.eventChannel.fireEvent("onEditFeatures", [{
                        $type : "Remove",
                        action : {
                            featureId : featureId,
                            layerViewId : layerViewId
                        }
                    }],
                    layerInfo.featureInfo.lastModified);
                });
            });
        }

        public getLayerStats (layerRequest: any, format: any, lastModified: number, opts?: ApiOpts) {
            if (!ValidateModule.checkMandatoryArg(layerRequest) ||
                !ValidateModule.checkMandatoryArg(format) ||
                !ValidateModule.checkInt(lastModified, true, true)) {
                return es.util.deferred.rejected();
            }

            format.$type = "LayerStatsFormat";
            const data = {
                layers : [layerRequest],
                format : format,
                version : lastModified
            };
            const getUrl = es.util.URLON.toUrl(data, es.api.getURL("featureview/" + format.$type)+"?");
            if (getUrl.length > this.rqBroker.MAX_URL_LENGTH) {
                return this.rqBroker.doPost({
                    url: es.api.getURL("featureview/" + format.$type)+"?",
                    data : data,
                    opts: opts
                });
            } else {
                return this.rqBroker.doGet({
                    url: getUrl,
                    opts: opts
                });
            }
        }

        /**
         * @function es.module.[resource].list
         * Get the resource from the server
         * @param layerViewId
         * @param criteria
         * @param sortOrder
         * @param offset
         * @param length
         * @param srid
         * @param opts
         * @return a jquery deferred object resolved when the request has completed
         */
        public list(layerViewId: number, criteria?:null|Criteria, sortOrder?:null|SortOrder, offset?:number, length?:number, srid?:number, opts?: ApiOpts): JQuery.Promise<Feature[]>{

            if (!ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }

            let getUrl = es.api.getListQueryURL(this.resourceName, criteria, sortOrder, offset, length, layerViewId, srid);

            // check if more than 500 results returned. If so, log a warning
            const doPaginationWarnCheck = function(rs?: any){
                if(es["isDeveloper"] && rs && rs.length >= 499){ // we could up this to 1000 but for feature selection 500 is probably adequate
                    es.log.warn("User has performed a selectByCriteria and 500 (or 499) results were returned which is our max. There may be more results in which case pagination is required.");
                }
            };

            if (getUrl.length > this.rqBroker.MAX_URL_LENGTH) {
                es.log.warn("Criteria passed to feature. list is too big (" + getUrl.length + ") to use in GET. Using POST instead");
                getUrl = es.api.getListQueryURL(this.resourceName, null, sortOrder, offset, length, layerViewId, srid);
                return this.rqBroker.doPost({
                    url: getUrl,
                    data : criteria,
                    opts: opts
                }).done(function(rs: Feature[]){
                    doPaginationWarnCheck(rs);
                });
            } else {
                return this.rqBroker.doGet({
                    url: getUrl,
                    opts: opts
                }).done(function(rs: Feature[]){
                    doPaginationWarnCheck(rs);
                });
            }
        }

        /**
         * @deprecated see #listLean
         */
        public listSimple(layerViewId: number, requiredAttributes: string[], criteria: Criteria|null= null, opts?: ApiOpts):
            JQuery.Deferred<{
                layers: {layerMetaId: number, layerViewId: number, features: any[]},
                styles: object[]}>
            {
            //const start = Date.now();
                return es.api.layerInfo.getForLayerView(layerViewId).then(layerInfo => {
                const data = {
                        format: {
                            $type: "SimpleJsonFormat",
                            requiredAttributes: requiredAttributes
                        },
                        layers: [{
                            id: layerViewId,
                            version: layerInfo.featureInfo.lastModified,
                            criteria: criteria
                        }],
                        version: 42
                    };

                    return this.getDataDfd(data, true, "", opts);
            }) as JQuery.Deferred<any>;
        }

        /**
         * Same as list simple, returns array of feats like:
         *
         *      {"id":73,"attrs":{"ACCOUNT":111202136,"TERRITORY":843435,"SALES12X":448,"SALES24X":456,"YTD_SALES":0,"PYTD_SALES":456,"MROPOTENTIAL":0,"SEPTEMBERSERIES":84,"SEPTEMBERCSG":843435,"LATITUDE":45.508243,"LONGITUDE":-90.710292},
         *      {"id":74,"attrs":{"ACCOUNT":111203449,"TERRITORY":843435,"SALES12X":42432,"SALES24X":40166,"YTD_SALES":20726,"PYTD_SALES":22400,"MROPOTENTIAL":0,"SEPTEMBERSERIES":84,"SEPTEMBERCSG":843435,"LATITUDE":35.97151,"LONGITUDE":-86.80937}
         *
         * However, data is transferred in more efficient way so that attr names are not duplicated but only included once at the top of the response instead. e.g.:
         *
         *      ["id","ACCOUNT","TERRITORY","SALES12X","SALES24X","YTD_SALES","PYTD_SALES","MROPOTENTIAL","SEPTEMBERSERIES","SEPTEMBERCSG","LATITUDE","LONGITUDE"],
         *      [73,111202136,843435,448,456,0,456,0,84,843435,45.508243,-90.710292],
         *      [74,111203449,843435,42432,40166,20726,22400,0,84,843435,35.97151,-86.80937]
         *
         * Data is then re-assembled on the client so that it is the same format as listSimple.
         *
         * Even with gzipping, this can lead to significant performance improvement.
         *
         * See: TOR9-14834
         *
         * @param layerViewId
         * @param requiredAttributes
         * @param criteria
         * @param opts
         * @returns {*}
         */
        public listLean (layerViewId: number, requiredAttributes?: string[], criteria: Criteria|null = null, opts?: ApiOpts): Promise<Feature[]> {
            return es.api.layerInfo.getForLayerView(layerViewId).then(layerInfo =>{
                const data = {
                    format: {
                        $type: "LeanJsonFormat",
                        requiredAttributes: requiredAttributes
                    },
                    layers: [{
                        id: layerViewId,
                        version: layerInfo.featureInfo.lastModified,
                        criteria
                    }],
                    version: 42
                };


                return this.getDataDfd(data, true, "listLean", opts).then(function(rs: any) {
                    return FeatureModule.unmarshallLeanResponse(rs, requiredAttributes);
                });
            }) as unknown as Promise<any>;
        }

        public listGeoJson(layerViewId: number, requiredAttributes: string[], criteria: Criteria|null = null, opts?: ApiOpts):
            JQuery.Deferred<{
                layers: {layerMetaId: number, layerViewId: number, features: any[]},
                styles: object[]}>
        {
            return es.api.layerInfo.getForLayerView(layerViewId, undefined, opts).then(layerInfo => {
                // format copied (more or less) from DatasourceManager.ts#getDatasourceUrl
                const data = {
                    format: {
                        $type: "GeoJsonFormat",
                        requiredAttributes: requiredAttributes,
                        decimals: 5
                    },
                    layers: [{
                        id: layerViewId,
                        version: layerInfo.featureInfo.lastModified,
                        criteria: criteria ?? {$type: "All"}
                    }],
                    version: 42
                };

                return this.getDataDfd(data, true, "listGeoJson", opts);
            }) as JQuery.Deferred<any>;
        }

        public static unmarshallLeanResponse(rs: [string[], ...(any[])[]], attrNames?: string[]): Feature[] {

            const attrsFactory = attrNames ? FeatureModule.createFactory(attrNames) : null;
            const feats: Feature[] = [];
            for (let i = 1; i < rs.length; i++) {
                const row = rs[i];
                const feat = {id: row[0], attrs:{}};
                if(attrsFactory && attrNames) {
                    const attrs = attrsFactory();
                    for (let x = 0; x < attrNames.length; x++) {
                        attrs[attrNames[x]] = row[x + 1];
                    }
                    feat.attrs = attrs;
                }

                feats.push(feat as Feature);
            }
            //runCount++;
            //runTotal += (Date.now() - start);
            //console.log("listLean DONE", (Date.now() - start) + "ms", "unmarshal", (Date.now() - start2) + "ms", "feat count", feats.length, "attrs", attrNames.length + 1 /*, "es.useAttrsFactory", es.useAttrsFactory, "runCount", runCount, "runAvg", (runTotal / runCount) */);
            return feats;
        }

        /**
         * @function es.module.[resource].list
         * Get the resource from the server
         * @param layerMetaId
         * @param criteria
         * @param sortOrder
         * @param offset
         * @param length
         * @param opts
         * @return a jquery deferred object resolved when the request has completed
         * @deprecated This is only used in the tests
         */
        listByLayerMetaId (layerMetaId:number, criteria: Criteria|null = null, sortOrder:null|SortOrder, offset: number, length: number, opts?: ApiOpts) {
            if (!ValidateModule.checkId(layerMetaId)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doGet({
                url: es.api.getListQueryURL(this.resourceName + "/layermeta", criteria, sortOrder, offset, length, layerMetaId),
                opts: opts
            });
            //return cache.doGetList(rqBroker, resourceName, criteria, sortOrder, offset, length, opts);
        }

        private static formatForUpdate(action: ActionType){
            action = es.util.object.deepCopy(action);
            let attrs = undefined;
            if ("attrs" in action) {
                attrs = action.attrs;
            }
            if(attrs){
                for(const key in attrs){
                    const value = attrs[key];
                    if(value instanceof Date){
                        attrs[key] = value.getTime();
                    }
                }
            }
            return action;
        }

        // This method uses eval to create a constructor which will produce objects with a particular set of properties.
        // This allows us to take advantage of hidden classes used by javascript engines like V8, which are way faster
        // than ad hoc objects
        // Note: this may have been true once but test on modern browser (chrome v93) show this makes negligible diff.
        // listLean DONE 186ms unmarshal 57ms feat count 139757 attrs 12 es.useAttrsFactory true      runCount 12 runAvg 215.75
        // listLean DONE 194ms unmarshal 65ms feat count 139757 attrs 12 es.useAttrsFactory undefined runCount 12 runAvg 227
        private static createFactory(attrNames: string[]){
            //es.log.info("new one");
            const toEval = ["{return{"];
            for(let i = 0; i < attrNames.length; i++){
                if(i){
                    toEval.push(",");
                }
                toEval.push(attrNames[i],":null");
            }
            toEval.push("};};");
            //eval(toEval.join(""));
            //console.log("createFactory", toEval.join(""));
            return new Function(toEval.join(""));
        }

        /**
         * Get layerviews feature count
         *
         * @param layerViewIds array of layer view ids
         * @param criteria (optional) criteria to be applied to the count (replaces existing layerView criteria)
         * @param opts
         */
        public count (layerViewIds: number[], criteria?: Criteria, opts?: ApiOpts) {
            if (!ValidateModule.checkIds(layerViewIds)) {
                return es.util.deferred.rejected();
            }

            let url = es.api.getURL(this.resourceName) + "/count?layerViewIds=" + es.util.url.stringify(layerViewIds);
            if(criteria){
                url+= "&criteria=" + es.util.url.stringify(criteria);
            }

            if (url.length > this.rqBroker.MAX_URL_LENGTH) {
                es.log.warn("Criteria passed to feature.countAll is too big (" + url.length + ") to use in GET. Using POST instead");
                url = es.api.getURL(this.resourceName) + "/count?layerViewIds=" + es.util.url.stringify(layerViewIds);
                return this.rqBroker.doPost({
                    url: url,
                    data : criteria,
                    opts: opts
                });
            } else {
                return this.rqBroker.doGet({
                    url: url,
                    opts: opts
                });
            }
        };

        /**
         * Bulk feature edit by specifying criteria.
         * Note: updates to address fields do not automatically trigger a re-geocode
         *
         * @param layerMetaId
         * @param layerViewId
         * @param attrs feature attributes as key-value pairs e.g. {"LAT": 52.312444, "LON": -7.271458, "CASTLE_NAME": "Arderra Castle", "CLAN_NAME": "Walsh"} note: the keys must be attribute names and not attribute titles
         * @param criteria the attribute that should be used as an identifier. e.g. can be {"es_id": 123} i.e. eSpatial's internal id, if know. Or, any other uniquely identifying attribute e.g. {"company": "eSpatial"}
         *
         * @param opts
         * @return jquery deferred server response is numOfFeaturesUpdated
         *
         * TODO: need to support layerViewId param for bulk editing on layermetea-less layerviews e.g. address
         */
        public bulkEdit(layerMetaId: number, layerViewId: number, attrs:Feature["attrs"], criteria: Criteria|null=null, opts?: ApiOpts) {

            // Create a json object to match com.espatial.es.serverapi.edit.Update i.e. {"featureId", "layerViewId", "layerMetaId", "attrs", "geom", "srid"}
            // Once we have layerMetaId we can then fetch featureSource and deduce all other required values (apart from the new attr values, obviously).
            const updateAction = {
                "$type": "Update",
                "featureId": -1,
                "layerViewId": (layerViewId ? layerViewId : -1),
                "layerMetaId": (layerMetaId ? layerMetaId : -1),
                "attrs": attrs, // only property used. all others are derived from layerMetaId
                "geom": null,
                "srid": null,
                "criteria": criteria
            };

            if(!opts){
                opts = {resolveWithXHR: true};
            } else {
                // @ts-ignore
                opts.resolveWithXHR = true;
            }

            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName) + "/bulk_edit",
                opts: opts,
                data: updateAction
            }).done((rs:any, jqXHR: JQuery.jqXHR)=>{
                const lastModified = jqXHR.getResponseHeader("es_feature_source_info_last_modified");
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerInfo_LayerMeta");
                this.cache.clear("Thematic");
                if(this.eventChannel) {
                    this.eventChannel.fireEvent("onBatchEditFeatures", rs, layerViewId, true, lastModified);
                }
            });
        }

        /**
         * Bulk removal of features by specifying criteria.
         *
         * @param layerMetaId
         * @param criteria the attribute that should be used as an identifier. e.g. can be {"es_id": 123} i.e. eSpatial's internal id, if know. Or, any other uniquely identifying attribute e.g. {"company": "eSpatial"}
         *
         * @param opts
         * @return jquery deferred server response is numOfFeaturesRemoved
         */
        public bulkRemove (layerMetaId: number, criteria: Criteria|null, opts?: ApiOpts) {
            if (!ValidateModule.checkId(layerMetaId) ||
                !ValidateModule.checkMandatoryArg(criteria)) {
                return es.util.deferred.rejected();
            }

            // Create a json object to match com.espatial.es.serverapi.edit.Update i.e. {"featureId", "layerViewId", "layerMetaId", "attrs", "geom", "srid"}
            // Once we have layerMetaId we can then fetch featureSource and deduce all other required values (apart from the new attr values, obviously).

            const url = es.api.getURL(this.resourceName) + "?" +
                "layerMetaId=" + layerMetaId;

            return this.rqBroker.doDelete({
                url: url,
                opts: opts,
                data: criteria
            });
        }

        /**
         * Fetch a list of featIds that have a geom.
         * Note: can only be used with MapScopeLayer for now as that's the only intended use case which is remove address
         * points which are _not_ Schedule lunch breaks or saved locations.
         * Can change to use with any other layers if ever req'd.
         *
         * @param layerViewId MapScopeLayerView where we are going to fetch ids from.
         *
         * @param opts
         * @return long featIds
         */
        public getFeatIdsWithGeom (layerViewId: number, opts?: ApiOpts) {
            if (!ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }

            const url = es.api.getURL(this.resourceName) + "/feats_with_geom/"+ layerViewId;

            return this.rqBroker.doGet({url: url, opts: opts});
        }

        /**
         * Calculate bounding box of features
         * @param layerViewId
         * @param criteria
         * @param opts
         * @return deferred
         */
        public bounds (layerViewId: number, criteria?: Criteria, opts?: ApiOpts) {
            if (!ValidateModule.checkId(layerViewId) ||
                !ValidateModule.checkMandatoryArg(criteria)) {
                return es.util.deferred.rejected();
            }

            const url = es.api.getURL(this.resourceName)
                + "/" + layerViewId + "/bounds";

            return this.rqBroker.doPost({
                url: url,
                data: criteria,
                opts: opts
            });
        }

        /**
         * Find nearest x services to a location by drive time or drive distance.
         *
         * @example
         *
         * es.api.feature.nearest(
         *     "DRIVE_TIME",
         *     558,
         *     ["COMPANY", "CITY_ADDRESS"],
         *     null,
         *     5,
         *     "KILOMETER",
         *     200,
         *     [-6.166, 53.406]
         * ).done(rs => {
         *     console.log(rs);
         *     // hilite feats on map
         *     if(rs.length){
         *         esApp.map.overlay.hilitePointByFeatKey(rs.map(f => "558_" + f.id), false);
         *         const points = rs.map(p => [p.geom[1], p.geom[2]]);
         *         const bounds = es.util.bounds.forPoints(points);
         *         esApp.map.overlay.drawBounds(bounds, null, 5000);
         *         esApp.map.baseMap.setBounds(bounds);
         *     }
         * });
         *
         * @param distanceType DRIVE_TIME, DRIVE_DISTANCE, EUCLIDEAN, LAT_LON currently supported
         * @param layerViewId lvId to query. TODO: only one lvId supported with this, may need to include more than one - need to confirm
         * @param requiredAttrs feat attrs to return (id automatically included)
         * @param searchStr search criteria for feature requiredAttrs cols type coercion enforced where possible and
         *                  value ignored where coercion is not possible (e.g. "5" on a boolean col).
         * @param numNeighbours number of nearest neighbours features to find
         * @param distanceUnit KILOMETER (default) or MILE
         * @param distance initial radius search to conduct to narrow down results to submit for drive time/distance query.
         *                 this is an unfortunate limitation but necessary for performance as generating
         *                 drive time/distances on-the-fly in bulk is a relatively slow process.
         * @param lonLat epicentre or 'from' location [Longitude, Latitude]
         * @param excludeFeatIds may want to exclude certain featId(s) from the results. For example, if query is being called
         *                       from the context of a feat you would probably not want the itself in the list of results.
         * @param excludeFeatsInScheduleId Auto-exclude any feats in this layer that are already in schedule
         * @param opts
         * @return {*}
         * TODO: accept another criteria (OR(like label) or (like attr))
         */
        public nearest (distanceType = "DRIVE_TIME", layerViewId: number, requiredAttrs: string[], searchStr: string,
                                numNeighbours = 12, distanceUnit = "KILOMETER", distance = 200, lonLat: number[], excludeFeatIds: null|number[], excludeFeatsInScheduleId?:null|number[], opts?: ApiOpts) {

            let url = es.api.getURL(this.resourceName) + "/nearest/" + layerViewId;

            url += "?distanceType=" + distanceType;

            if (requiredAttrs && Array.isArray(requiredAttrs) && requiredAttrs.length > 0) {
                url += "&requiredAttrs=" + requiredAttrs;
            }

            if (searchStr) {
                url += "&searchStr=" + encodeURIComponent(searchStr);
            }

            if (excludeFeatIds && Array.isArray(excludeFeatIds) && excludeFeatIds.length > 0) {
                url += "&excludeFeatIds=" + excludeFeatIds;
            }

            if (excludeFeatsInScheduleId) {
                url += "&excludeFeatsInScheduleId=" + excludeFeatsInScheduleId;
            }

            // nearestCriteria initial spatial criteria to narrow down search. this is used as an initial approximation
            // which is first fetched with an extra 50% of results by straight-line distance, then drive times/distances
            // are fetched for all of those just the required number of results (as specified in nearestCriteria)
            // are returned and sorted by distanceType.
            const nearestCriteria = {
                $type: "Nearest",
                name: "",
                numNeighbours: numNeighbours,
                distance: distance,
                unit: distanceUnit,
                geometry: ['PT', ...lonLat]
            };

            return this.rqBroker.doPost({
                url: url,
                data: nearestCriteria,
                opts: opts
            });
        }

        /**
         * Get distances for specified features.
         *
         * @example
         *         es.api.feature.distance(
         *             "DRIVE_TIME",
         *             188,
         *             ["COMPANY", "CONTACT"],
         *             "KILOMETER",
         *             [-87.69239, 41.97644],
         *             [1, 2, 3]
         *         ).done(rs => {
         *             console.log(rs);
         *             // hilite feats on map
         *             esApp.map.overlay.hilitePointByFeatKey(rs.map(f => "188_" + f.id), false);
         *         });
         *
         * @param distanceType DRIVE_TIME, DRIVE_DISTANCE, EUCLIDEAN, LAT_LON currently supported
         * @param layerViewId lvId to query. TODO: only one lvId supported with this, may need to include more than one - need to confirm
         * @param requiredAttrs feat attrs to return (id automatically included)
         * @param _distanceUnit KILOMETER (default) or MILE
         * @param latLon epicentre or 'from' location
         * @param esIds may want to exclude certain featId(s) from the results. For example, if query is being called
         *              from the context of a feat you would probably not want the itself in the list of results.
         * @param opts
         * @return {*}
         */
        public distance (distanceType = "DRIVE_TIME", layerViewId: number, requiredAttrs:string[], _distanceUnit = "KILOMETER", latLon: number[], esIds:number[], opts?: ApiOpts) {

            let url = es.api.getURL(this.resourceName) + "/distance/" + layerViewId;

            url += "?distanceType=" + distanceType;

            url += "&lon=" + latLon[0];
            url += "&lat=" + latLon[1];

            if (requiredAttrs) {
                url += "&requiredAttrs=" + requiredAttrs;
            }

            if (esIds) {
                url += "&esIds=" + esIds;
            }

            return this.rqBroker.doGet({url: url, opts: opts});
        }
    }
